
import { Vue, Options } from "vue-class-component";
import DownloadReports from "./components/download-reports.vue";
import { KulionerController } from "../../controllers/KulionerController";
import { KulionerListRequest } from "@/data/payload/api/KulionerApiRequest";
import { KulionerEntites, KulionerOrder } from "@/domain/entities/Kulioner";
import { MainAppController } from "../../controllers/MainAppController";
import generateKulionerReceipt from "./components/receipt";
import TabFilter from "./components/tab-filter.vue";
import FooterListKulioner from "./components/FooterListKulioner.vue";
import { Pagination } from '@/domain/entities/Pagination';
import PaginationV3 from "@/app/ui/components/pagination-v3/index.vue";
import { CLIENT_ERROR, INTERNET_ERROR, INTERNAL_SERVER_ERROR, SPECIAL_ERROR, formatDateNormal } from '@/app/infrastructures/misc/Utils';
import Checkbox from "primevue/checkbox";
import Loading from "@/app/ui/components/loading/index.vue";
import { defineAsyncComponent } from "vue";

const CardListKulioner = defineAsyncComponent({
  loader: () =>
    import("@/app/ui/views/kulioner/components/card-list-kulioner.vue")
});

@Options({
  components: {
    CardListKulioner,
    DownloadReports,
    TabFilter,
    FooterListKulioner,
    PaginationV3,
    Checkbox,
    Loading
  }
})
export default class KulionerList extends Vue {
  CLIENT_ERROR = CLIENT_ERROR;
  INTERNAL_SERVER_ERROR = INTERNAL_SERVER_ERROR;
  INTERNET_ERROR = INTERNET_ERROR;
  SPECIAL_ERROR = SPECIAL_ERROR;

  errorState = "";
  searchByOptions = [
    {
      label: "Nama merchant",
      value: "MERCHANT_NAME"
    },
    {
      label: "Nama customer",
      value: "RECIPIENT_NAME"
    },
    {
      label: "No telp merchant",
      value: "MERCHANT_PHONE_NUMBER"
    },
    {
      label: "Order ID",
      value: "ORDER_ID"
    },
    {
      label: "Nomor STT",
      value: "STT_NO"
    },
  ]

  listKulioner: KulionerEntites = new KulionerEntites();
  checkedData: KulionerOrder[] = [];
  currentTab = "";
  filter = {
    pagination: new Pagination(1, 50, 0, 0),
    dateRange: {
      start: new Date(new Date().setDate(new Date().getDate() - 30)),
      end: new Date()
    },
    searchBy: this.searchByOptions.find(option => option.value === "ORDER_ID"),
    searchValue: ""
  }
  listSummaryStatus = {};
  isLoadingFetchList = false;

  mounted() {
    this.autoMoveTab();
  }

  autoMoveTab() {
    const tab = this.$route.query?.tab;
    if (tab) {
      this.currentTab = tab as string;
      MainAppController.showLoading();
    }
  }

  get isShowCheckAll() {
    const status = {
      ORDWTC: true,
      ORDBKD: true,
      ORDDLV: true,
      ORDRCV: true,
      CNCWRF: true,
    } as any

    const isShow = status[this.currentTab as any] ?? false;

    return isShow && !!this.listKulioner.data.order.length;
  }

  get isCheckedAll() {
    const orderIds = this.listKulioner.data.order.map(order => order.koId);
    const checkedOrderIds = this.checkedData.map(order => order.koId);

    if (!checkedOrderIds.length) return false;

    return (
      orderIds.findIndex(id => {
        return checkedOrderIds.findIndex(checked => checked === id) === -1;
      }) < 0
    );
  }

  get isLoadingModal() {
    return MainAppController.loadingModal;
  }

  get isShowLoadingFetchListModal() {
    return this.isLoadingFetchList && !this.isLoadingModal
  }

  handleCheck(val: KulionerOrder) {
    this.checkedData.push(val);
  }

  handleUnCheck(val: KulionerOrder) {
    this.checkedData = this.checkedData.filter(
      (item) => item.koId !== val.koId
    );
  }

  async getListKulioner() {
    try {
      this.isLoadingFetchList = true;
      this.errorState = "";
      this.listKulioner.data.order = [];
      const res = await KulionerController.getKulionerList(
        new KulionerListRequest({
          endDate: formatDateNormal(this.filter.dateRange.end, "YYYY-MM-DDTHH:mm:ssZ"),
          startDate: formatDateNormal(this.filter.dateRange.start, "YYYY-MM-DDTHH:mm:ssZ"),
          status: this.currentTab,
          q: this.filter.searchValue,
          qType: this.filter.searchBy?.value || "",
          page: this.filter.pagination.page,
        })
      );
      await this.getListSummaryStatus();

      this.listKulioner.pagination = res.pagination;
      this.listKulioner.data.order = res.data.order;

      if (!this.listKulioner.data.order.length) {
        this.errorState = "empty"

        if (this.filter.searchValue) {
          this.errorState = SPECIAL_ERROR;
        }
      }
    } catch (error) {
      this._handlerErrorGetList(error);
    } finally {
      this.isLoadingFetchList = false;
    }
  }

  async onPrint() {
    await generateKulionerReceipt(this.listKulioner.data.order);
  }

  _handlerErrorGetList(error: any) {
    if (!error.response) {
      this.errorState = INTERNET_ERROR;
      return;
    }
    if (error.response.status > 500) {
      this.errorState = CLIENT_ERROR;
      return;
    }

    this.errorState = INTERNAL_SERVER_ERROR;
  }

  changeTab() {
    // Remove tab from URL without page reload
    this.$router.replace({ query: {} });

    this.filter.pagination.page = 1;
    this.resetList();
    this.checkedData = [];
    this.getListKulioner();
  }

  onSearch(search: any) {
    this.filter.searchValue = search;
    this.filter.pagination.page = 1;
  }

  onSearchBy(searchBy: any) {
    this.filter.pagination.page = 1;
    this.filter.searchBy = searchBy;
    if (this.isLoadingFetchList) return;
    this.getListKulioner();
  }

  setPeriod(value: any) {
    this.filter.pagination.page = 1;
    if (value[0] && value[1]) {
      this.filter.dateRange.start = value[0];
      this.filter.dateRange.end = value[1];
    }

    if (!value[0] && !value[1]) {
      this.filter.dateRange = {
        start: new Date(new Date().setDate(new Date().getDate() - 30)),
        end: new Date()
      }
    }

    this.getListKulioner();
  }

  async toPage(page: number) {
    if (page === this.filter.pagination.page) {
      return;
    }
    this.filter.pagination.page = page;
    this.checkedData = [];
    await this.getListKulioner();
    this.$nextTick(() => {
      const el = document.getElementById("kulioner-list-content-parent");

      if (el) {
        el.scroll({top: 0, behavior: "smooth"})
      }
    })
  }

  onCheckAll() {
    if (this.isCheckedAll) {
      this.checkedData = [];
    } else {
      this.checkedData = this.listKulioner.data.order;
    }
  }

  resetList() {
    this.listKulioner = new KulionerEntites();
  }

  resetFilter() {
    this.filter.searchValue = ""
    this.filter.searchBy = this.searchByOptions.find(option => option.value === "ORDER_ID");
  }

  async getListSummaryStatus() {
    this.listSummaryStatus = await KulionerController.getListSummaryStatus(
      new KulionerListRequest({
        endDate: formatDateNormal(this.filter.dateRange.end, "YYYY-MM-DDTHH:mm:ssZ"),
        startDate: formatDateNormal(this.filter.dateRange.start, "YYYY-MM-DDTHH:mm:ssZ"),
        q: this.filter.searchValue,
        qType: this.filter.searchBy?.value || "",
        page: this.filter.pagination.page
      })
    )
  }

  onScrollListParent(e: any) {
    (this.$refs.virtualScroll as any).handleParentScroll(e);
  }
}
